import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';

/**
 * Portées demandées au hosted UI.
 *
 * `aws.cognito.signin.user.admin` n'est pas décorative : le back lit le profil par `getUser`,
 * qui l'exige. Sans elle une connexion par annuaire aboutit puis échoue au premier appel, ce
 * qui se lit comme une panne sans cause. La commande d'ouverture de surface pose la même
 * liste sur l'app client ; les deux doivent rester d'accord.
 */
const OAUTH_SCOPES = ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'];

/**
 * Le domaine hosted UI tel qu'Amplify l'attend : un hôte nu.
 *
 * Le paramètre du tenant le porte avec son schéma, parce qu'il sert aussi à construire des
 * URL ailleurs. Amplify, lui, recompose l'URL et refuse un schéma en double.
 */
function hostedUiHost() {
  const domain = window?.cognito?.hosted_ui_domain;

  return domain ? String(domain).replace(/^https?:\/\//, '').replace(/\/+$/, '') : '';
}

/**
 * Le bloc OAuth du tenant, ou rien du tout.
 *
 * Rien : un tenant sans surface SSO doit démarrer exactement comme avant. Composer un bloc
 * vide ferait échouer Amplify à la configuration, donc sur la page de connexion native.
 *
 * Les URI sont dérivées de l'origine courante, et doivent correspondre à celles enregistrées
 * sur l'app client — que le back construit depuis `app.url`. Un désaccord ne se voit pas ici :
 * l'annuaire renvoie une erreur d'autorisation, que `redirectFailure()` donne à lire.
 */
function oauthConfiguration() {
  const domain = hostedUiHost();

  if (!domain) {
    return {};
  }

  return {
    loginWith: {
      oauth: {
        domain,
        scopes: OAUTH_SCOPES,
        // La page de connexion, et non la racine : c'est elle qui lit un refus rapporté en
        // paramètres d'URL, et un visiteur non authentifié renvoyé de la racine y arriverait
        // sans sa chaîne de requête.
        redirectSignIn: [`${window.location.origin}/login`],
        redirectSignOut: [`${window.location.origin}/login`],
        responseType: 'code',
      },
    },
  };
}

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: window?.cognito?.user_pool_id,
      userPoolClientId: window?.cognito?.app_client_id,
      region: window?.cognito?.region,
      ...{
        userPoolEndpoint: window?.cognito?.user_pool_endpoint
          ? window.location.origin + window.cognito.user_pool_endpoint
          : undefined,
      },
      ...oauthConfiguration(),
    },
  },
});

const amplifyConfig = Amplify.getConfig();

export { amplifyConfig };

/**
 * L'échec que l'annuaire rapporte sur l'URI de retour, s'il y en a un.
 *
 * OAuth 2.0 ramène un refus en paramètres de requête plutôt qu'en exception : `error` et
 * `error_description`. Les lire suffit à connaître la cause, sans rien attendre et sans
 * dépendre d'Amplify — un retour en échec n'établit aucune session, donc aucun évènement
 * applicatif ne le signalerait autrement.
 *
 * Journalisé ici plutôt que chez l'appelant : une erreur de configuration doit laisser une
 * trace même si la page qui reçoit ce retour choisit de n'en rien afficher.
 *
 * @returns {{error: string, description: string}|null}
 */
let exchangeFailure = null;

/**
 * Qui attend d'apprendre un refus de retour.
 *
 * L'échec de l'échange arrive APRÈS le montage de la page qui doit l'afficher : Amplify ne
 * le rapporte qu'au terme de son aller-retour vers le token endpoint. Une lecture faite au
 * montage tombe donc toujours avant, et l'écran restait muet sur le seul cas qu'il avait à
 * dire. On prévient donc, au lieu de se laisser interroger une fois.
 */
const failureWatchers = new Set();

/**
 * L'échec du second temps, que l'URL ne porte pas.
 *
 * Une fois le code ramené, Amplify l'échange contre des jetons. Cet échange peut échouer
 * seul — `invalid_client` au token endpoint, un `state` ou un PKCE qui ne correspond pas —
 * et rien n'en paraît : pas de paramètre `error`, pas de session, pas d'exception chez
 * l'appelant. Amplify ne le rapporte que par l'évènement `signInWithRedirect_failure`, qu'on
 * retient ici pour que `redirectFailure()` le serve comme s'il était venu de l'URL.
 */
Hub.listen('auth', ({ payload }) => {
  if (payload.event !== 'signInWithRedirect_failure') {
    return;
  }

  const description = payload.data?.error?.message ?? String(payload.data?.error ?? '');
  exchangeFailure = { error: 'exchange_failed', description };

  // eslint-disable-next-line no-console
  console.error('Directory sign-in failed while exchanging the code', { description });

  failureWatchers.forEach((watcher) => watcher(exchangeFailure));
});

export function redirectFailure() {
  const parameters = new URLSearchParams(window.location.search);
  const error = parameters.get('error');

  if (!error) {
    // L'échange, lui, n'a rien laissé dans l'URL : c'est le Hub qui l'a rapporté.
    return exchangeFailure;
  }

  const description = parameters.get('error_description') ?? '';

  // eslint-disable-next-line no-console
  console.error('Directory sign-in was refused at the authorization step', { error, description });

  return { error, description };
}

/**
 * Prévient d'un retour refusé : celui qui est déjà là, puis celui qui se saura plus tard.
 *
 * Appelée tout de suite quand l'URL en porte un — c'est le refus de l'étape d'autorisation,
 * qui est là dès le premier rendu. L'échec de l'échange, lui, n'arrive qu'ensuite : il passe
 * par l'abonnement.
 *
 * @param {(failure: {error: string, description: string}) => void} watcher
 * @returns {() => void} De quoi se désabonner, à appeler en quittant la page.
 */
export function onRedirectFailure(watcher) {
  const alreadyKnown = redirectFailure();

  if (alreadyKnown) {
    watcher(alreadyKnown);
  }

  failureWatchers.add(watcher);

  return () => failureWatchers.delete(watcher);
}

/**
 * Oublie l'échec retenu, une fois la page qui devait le lire passée.
 */
export function clearRedirectFailure() {
  exchangeFailure = null;
}

export default {
  install: (app) => {
    // eslint-disable-next-line no-param-reassign
    app.config.globalProperties.$auth = {
      confirmSignIn,
      confirmResetPassword,
      fetchAuthSession,
      getCurrentUser,
      resetPassword,
      signIn,
      signInWithRedirect,
      signOut,
      updatePassword,
    };
  },
};
